/**
 * Main App Component
 *
 * Sets up Shopify Polaris provider, data health context, and routing.
 * Includes root-level error boundary for graceful error handling.
 */

import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { FronteggProvider, useAuth } from '@frontegg/react';
import { AppProvider, InlineStack, Spinner } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';

import { ErrorBoundary } from './components/ErrorBoundary';
import { RootErrorFallback } from './components/ErrorFallback';
import { AuthGuard } from './components/AuthGuard';
import { DataHealthProvider } from './contexts/DataHealthContext';
import { AppShell } from './components/layout/AppShell';
import {
  FRONTEGG_BASE_URL,
  FRONTEGG_CLIENT_ID,
  fronteggEnabled,
} from './config/frontegg';
import { resolveOnboardingDestination } from './services/onboardingApi';
import { provisionTenant } from './services/tenantsApi';
import Actions from './pages/Actions';
import AdminPlans from './pages/AdminPlans';
import Analytics from './pages/Analytics';
import Billing from './pages/Billing';
import BillingSuccess from './pages/BillingSuccess';
import Paywall from './pages/Paywall';
import InsightsFeed from './pages/InsightsFeed';
import ApprovalsInbox from './pages/ApprovalsInbox';
import AgentSettings from './pages/AgentSettings';
import BeliefInspector from './pages/BeliefInspector';
import LLMSettings from './pages/LLMSettings';
import AgentEnablement from './pages/AgentEnablement';
import WhatsNew from './pages/WhatsNew';
import SyncHealth from './pages/SyncHealth';
import Connectors from './pages/Connectors';
import DataUploads from './pages/DataUploads';
import SharedDashboard from './pages/SharedDashboard';
// Every /marketing-agents route lives together (spec #44 §3): the
// workspace at the root, the catalog moved to /specialists, the specialist
// detail at /s/:key, the persona variant at /a/:domain, and the old
// /marketing-agents/:key redirecting into /s/:key so no bookmark breaks.
// Mounting them as one fragment keeps the static-before-dynamic ordering
// from drifting apart.
import { WorkspaceRoutes } from './pages/Workspace';
import Dashboards from './pages/Dashboards';
import Ask from './pages/Ask';
import Alerts from './pages/Alerts';
import Welcome from './pages/Welcome';
import NotFound from './pages/NotFound';

function CenteredSpinner({ label }: { label: string }) {
  return (
    <div style={{ paddingTop: '4rem' }}>
      <InlineStack align="center">
        <Spinner size="large" accessibilityLabel={label} />
      </InlineStack>
    </div>
  );
}

/** `/` (T9): onboarding-incomplete visitors land on /welcome, everyone
 * else on /dashboards — resolveOnboardingDestination is the single
 * source of truth /billing/success's continuation button also uses. */
function RootRoute() {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    resolveOnboardingDestination()
      .then((destination) => {
        if (!cancelled) navigate(destination, { replace: true });
      })
      .catch((err) => {
        // resolveOnboardingDestination already catches internally and
        // never rejects today, but this route must not depend on that
        // staying true forever — self-standing against a future change
        // there, or an error navigate() itself throws.
        console.error('Failed to resolve root route:', err);
        if (!cancelled) navigate('/welcome', { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return <CenteredSpinner label="Loading" />;
}

/** Idempotent tenant provisioning (T9), once per identity, behind the
 * auth boundary. Tolerates 409/already-provisioned and 401 (tenantsApi's
 * provisionTenant already treats both as a no-op); any other failure is
 * logged but never blocks the tenant routes from rendering — provisioning
 * already ran once at signup, so a bootstrap-time hiccup here is not the
 * merchant's first provisioning attempt.
 *
 * Keyed by the authenticated tenant id (not a bare boolean): a bare
 * "already provisioned" flag would stay stuck true across a logout and a
 * different tenant logging in on the same tab, silently skipping THEIR
 * provisioning.
 *
 * UNPROVISIONED (not `null`) is the initial sentinel, deliberately unequal
 * to every possible `identity` value INCLUDING `null` (no tenantId yet —
 * user still loading, or genuinely absent). A `null`-initialized sentinel
 * collided with a `null` identity on the very first render: `ready` would
 * initialize to `true`, the effect would immediately take the "already
 * provisioned" branch, and provisionTenant() would never run — silently,
 * no spinner, no log, self-healing only if `identity` later changed to a
 * truthy value (which nothing guaranteed). A `null` identity now
 * provisions like any other value; the 401 that draws is already a no-op
 * in tenantsApi.provisionTenant. */
const UNPROVISIONED = Symbol('unprovisioned');
let provisionedFor: string | null | typeof UNPROVISIONED = UNPROVISIONED;

export function TenantBootstrap({ children }: { children: React.ReactNode }) {
  // No cast: AuthState.user (the real return type of useAuth(), from
  // @frontegg/redux-store's auth/interfaces.d.ts) is `User | null | undefined`,
  // and User's IBaseGetUserResponse ancestor carries a non-optional
  // `tenantId: string` — tsc verifies this field exists for real.
  const { user } = useAuth();
  const identity = user?.tenantId ?? null;
  const [ready, setReady] = useState(provisionedFor === identity);

  useEffect(() => {
    if (provisionedFor === identity) {
      // Also the React 18 StrictMode dev double-invoke path: the first
      // (real) mount already set provisionedFor synchronously below,
      // before this second mount runs, so this branch — not a second
      // provisionTenant() call — is what it hits. It must still flip
      // `ready`, or a component that never rendered its first mount's
      // .finally() (StrictMode's simulated unmount) is stuck spinning
      // forever — the bug this replaces.
      setReady(true);
      return;
    }
    provisionedFor = identity;
    provisionTenant()
      .catch((err) => {
        console.error('Tenant provisioning failed:', err);
      })
      .finally(() => setReady(true));
    // No cleanup/cancelled guard on setReady: React 18 no longer warns
    // on a state update after unmount, and this must fire unconditionally
    // for the StrictMode remount above to observe `ready` flip.
  }, [identity]);

  if (!ready) {
    return <CenteredSpinner label="Setting up your workspace" />;
  }
  return <>{children}</>;
}

function App() {
  // Public share links render before the auth boundary: the token in the
  // query string IS the authorization (resolved server-side), and a
  // recipient has no Frontegg session to satisfy AuthGuard with. The
  // boundary here is public-safe on purpose — recipients get a generic
  // message and a retry, never error.message or a component stack
  // (PR #11 review).
  if (window.location.pathname.replace(/\/+$/, '') === '/share') {
    return (
      <ErrorBoundary
        fallbackRender={() => (
          <AppProvider i18n={enTranslations}>
            <div style={{ padding: 32, textAlign: 'center' }}>
              <p>This shared dashboard could not be displayed.</p>
              <button onClick={() => window.location.reload()}>
                Try again
              </button>
            </div>
          </AppProvider>
        )}
        onError={(error) => {
          console.error('Shared dashboard failed to render:', error);
        }}
      >
        <AppProvider i18n={enTranslations}>
          <SharedDashboard />
        </AppProvider>
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary
      fallbackRender={({ error, errorInfo, resetErrorBoundary }) => (
        <AppProvider i18n={enTranslations}>
          <RootErrorFallback
            error={error}
            errorInfo={errorInfo}
            resetErrorBoundary={resetErrorBoundary}
          />
        </AppProvider>
      )}
      onError={(error, errorInfo) => {
        // Log error to console (could be extended to send to error tracking service)
        console.error('Root error boundary caught error:', error);
        console.error('Component stack:', errorInfo.componentStack);
      }}
    >
      <AppProvider i18n={enTranslations}>
        <DataHealthProvider>
          <BrowserRouter>
            {(() => {
              const routed = (
                <AppShell>
                  <Routes>
                    <Route path="/admin/plans" element={<AdminPlans />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/billing/success" element={<BillingSuccess />} />
                    <Route path="/paywall" element={<Paywall />} />
                    <Route path="/insights" element={<InsightsFeed />} />
                    <Route path="/approvals" element={<ApprovalsInbox />} />
                    <Route path="/actions" element={<Actions />} />
                    <Route path="/agents" element={<AgentSettings />} />
                    <Route path="/beliefs" element={<BeliefInspector />} />
                    <Route path="/settings/llm" element={<LLMSettings />} />
                    <Route
                      path="/agents/enablement"
                      element={<AgentEnablement />}
                    />
                    <Route path="/whats-new" element={<WhatsNew />} />
                    <Route path="/sync-health" element={<SyncHealth />} />
                    <Route path="/connectors" element={<Connectors />} />
                    <Route path="/data-uploads" element={<DataUploads />} />
                    {WorkspaceRoutes}
                    <Route path="/dashboards" element={<Dashboards />} />
                    <Route path="/ask" element={<Ask />} />
                    <Route path="/alerts" element={<Alerts />} />
                    <Route path="/welcome" element={<Welcome />} />
                    <Route path="/" element={<RootRoute />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </AppShell>
              );
              // Hosted-login gate only when the deploy enables Frontegg —
              // AuthGuard's hooks need the provider mounted in main.tsx.
              // TenantBootstrap runs inside it: it needs a real
              // authenticated session, and legacy mode (no Frontegg, no
              // guard — local dev, vitest, the API-stubbed Playwright e2e)
              // never had a provisioning step to preserve.
              // FronteggProvider is mounted HERE, inside <BrowserRouter>,
              // not above <App/> in main.tsx. @frontegg/react's provider
              // creates its own <BrowserRouter> when it is not already in a
              // router context; above the app that produced a second router
              // and React Router's invariant took down the whole page with
              // the root error boundary (caught on staging 2026-08-23).
              // Inside the router the SDK takes its Connector branch and
              // reuses this one. See fronteggRouterComposition.test.tsx.
              return fronteggEnabled ? (
                <FronteggProvider
                  contextOptions={{
                    baseUrl: FRONTEGG_BASE_URL as string,
                    clientId: FRONTEGG_CLIENT_ID as string,
                  }}
                  hostedLoginBox
                  authOptions={{ keepSessionAlive: true }}
                >
                  <AuthGuard>
                    <TenantBootstrap>{routed}</TenantBootstrap>
                  </AuthGuard>
                </FronteggProvider>
              ) : (
                routed
              );
            })()}
          </BrowserRouter>
        </DataHealthProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
